//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import ObservationTypeModel from './observation-type.model';

/**
 * Service for managing observation types data (Master Data).
 *
 * Provides methods to fetch and manage Observation-types-related data.
 * Inherits CRUD operations and HTTP utilities from the generic {@link Service} class.
 *
 * @module ObservationTypesService
 */
@Injectable({
  providedIn: 'root'
})
export class ObservationTypesService extends Service<ObservationTypeModel> {

  /**
   * Stores ongoing HTTP request subscriptions.
   * Used to cancel active requests and prevent memory leaks.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructs an instance of ObservationTypesService.
   *
   * @param {HttpClient} httpClient - The HttpClient service for making HTTP requests.
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/blubay/observationtype/');
  }

  /**
   * Fetches the complete list of Observation types (Master Data).
   *
   * @method
   * @returns {Promise<Array<ObservationTypeModel>>} A Promise resolving with an array of ObservationTypeModel objects.
   * @throws {HttpErrorResponse} Throws an HTTP error if the request fails.
   *
   * Description:
   * This method is typically called in {@link ObservationTypesComponent} to retrieve the full Observation types list.
   */
  public getAllObservationsList(): Promise<Array<ObservationTypeModel>> {
    return new Promise<Array<ObservationTypeModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<ObservationTypeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000&order=new`, this.httpOptions)
        .subscribe(
          response => resolve(response),
          (error: HttpErrorResponse) => reject(error)
        );
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Cancels all ongoing HTTP requests by unsubscribing from their observables.
   * Ensures proper cleanup and prevents memory leaks.
   *
   * Typically called in components when they are destroyed to cancel pending requests.
   *
   * @method
   * @returns {void}
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach(request => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
